import {useState, useContext, useEffect} from 'react';
import styles from '../Applicants/css/display-applicant.module.css';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {AiOutlineSearch as SearchIcon} from 'react-icons/ai';
import {IoReload as ReloadIcon} from 'react-icons/io5';
import apiCall from '../../../utils/apiCall';
import { CircularProgress, styled } from '@mui/material';
import { AxiosError } from 'axios';
import { FilterDropDown} from '../../../ui-components';
import InterviewTable from './Table';
import { ExNyRoute,PrRoute, ExRoute, AcRoute, MiRoute,  CoRoute } from '../../../utils/allRouteId';
import BatchReinviteModal from './Modal/BatchReinviteModal';
import { useSelector } from 'react-redux';
import {RootState} from '../../../redux/store';
import { NotificationContext } from '../../../providers/notification'; 
import ReInvites from './Modal/ReInvite';



interface Interview {
  ApplicantId: string;
  FullName: string;
  Email: string;
  PhoneNumber: string;
  Comment: string;
}

interface PostInterview {
  ApplicantId: string[];
  Activity: string;
}

// Define an interface for full Interview object
interface InterviewFull {
  ApplicantId: string;
  FullName: string;
  Email: string;
  PhoneNumber: string;
  HasAttend: boolean;
  IsSucess: boolean;
  Comment: string;
}

// Props interface remain the same for DisplayInterview
interface DisplayInterviewProps {
  setRoute: React.Dispatch<React.SetStateAction<{ name: string; val: number }>>;
  route?: { name: string; val: number };
  showReinviteModal: boolean;
  handleCloseReinvite: () => void;
}

const DisplayInterview = ({
  setRoute,
  route,
  showReinviteModal,
  handleCloseReinvite,
}: DisplayInterviewProps) => {
  const [searchData, setSearchData] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();

  // fetch interview list
  const getInterview = async (): Promise<InterviewFull[]> => {
    const resp = await apiCall().get('/Activity/GetApplicantPendingInterview');
    return resp.data.Data;
  };

  const getInterviewMutation = useMutation(getInterview, { retry: 0 });
  const getInterviewError = getInterviewMutation.error as AxiosError;

  const ReinviteMutation = useMutation(
    (payload: InterviewFull[]) =>
      apiCall().post('/Activity/ReInvitePendingApplicants', payload),
    { retry: 0 }
  );

  // source of truth
  const InterviewList = getInterviewMutation.data ?? [];

  // search filter
  const displayedData =
    searchData.length > 0
      ? InterviewList.filter((i) =>
          Object.values(i)
            .join(' ')
            .toLowerCase()
            .includes(searchData.toLowerCase())
        )
      : InterviewList;

  // submit handler
  const handleReinviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedIds.length === 0) {
      showNotification({
        message: 'Select Applicants To Invite',
        type: 'error',
      });
      return;
    }

    const selectedApplicants = InterviewList.filter((item) =>
      selectedIds.includes(item.ApplicantId)
    );

    if (selectedApplicants.length === 0) {
      showNotification({
        message: 'No matching applicants found',
        type: 'error',
      });
      return;
    }

    try {
      await ReinviteMutation.mutateAsync(selectedApplicants, {
        onSuccess: () => {
          showNotification({
            message: 'Reinvitation Successful',
            type: 'success',
          });
          queryClient.invalidateQueries(['get-Interview-Invite']);
          handleCloseReinvite();
          setSelectedIds([]); // clear selection after success
        },
        onError: (err: any) => {
          showNotification({
            message:
              err?.response?.data?.Message ||
              err.message ||
              'Error sending invites',
            type: 'error',
          });
        },
      });
    } catch (err: any) {
      showNotification({
        message: err?.message || 'Error in submission',
        type: 'error',
      });
    }
  };

  useEffect(() => {
    getInterviewMutation.mutateAsync();
    setSelectedIds([]); // reset selection when route changes
  }, [route]);

  return (
    <>
      {/* Top search UI */}
      <TopWrapper>
        <label className={styles.searchForm}>
          <span className={styles.sI}>
            <SearchIcon />
          </span>
          <input
            value={searchData}
            onChange={(e) => setSearchData(e.target.value)}
            type="text"
            placeholder="Search"
          />
          <button
            className={styles.rBtn}
            type="button"
            onClick={() => getInterviewMutation.mutateAsync()} // reload button
          >
            <ReloadIcon />
          </button>
        </label>
      </TopWrapper>

      {getInterviewMutation.isLoading ? (
        <CircularProgress
          sx={{ fontSize: '1rem', margin: '2rem auto', display: 'block' }}
        />
      ) : getInterviewMutation.isError ? (
        <StyledError>{getInterviewError?.message}</StyledError>
      ) : displayedData.length > 0 ? (
        <InterviewTable data={displayedData} routeId={route?.val} />
      ) : (
        <div>No Result</div>
      )}

      <BatchReinviteModal
        open={showReinviteModal}
        cancel={handleCloseReinvite}
        header="Re-Invite Applicant"
        maxWidth="1300px"
        content={
          <ReInvites
            data={displayedData}
            isLoading={ReinviteMutation.isLoading}
            handleReInviteSubmit={handleReinviteSubmit}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            searchData={searchData}
            setSearchData={setSearchData}
          />
        }
      />
    </>
  );
};


export default DisplayInterview;

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`

const TopWrapper = styled('div')`
  display:flex;
  gap:10px;
  align-items: center;
  margin: 10px 0px 48px 0px;
`
